import { awsClient, setAuthToken, clearAuthToken } from './awsClient';

const authBasePath = '/auth';

const buildDiscordAvatarUrl = (user) => {
  if (!user?.id || !user?.avatar) return null;
  return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`;
};

const normalizeDiscordUser = (user) => {
  if (!user || typeof user !== 'object') return null;

  const avatarUrl = user.avatar_url || user.discord_avatar || buildDiscordAvatarUrl(user);
  const discordDisplayName =
    user.display_name ||
    user.global_name ||
    user.discord_display_name ||
    null;
  const preferredName =
    discordDisplayName ||
    user.discord_username ||
    (typeof user.full_name === 'string' && user.full_name.toLowerCase() !== 'user' ? user.full_name : null) ||
    user.username ||
    'User';
  const campusType = user.campus_type || user.campusType || 'main';

  return {
    ...user,
    display_name: discordDisplayName || preferredName,
    global_name: user.global_name || discordDisplayName || null,
    avatar_url: avatarUrl,
    discord_avatar: avatarUrl,
    discord_username: user.discord_username || user.username || preferredName,
    full_name: preferredName,
    campus_type: campusType,
    campusType,
  };
};

/** @param {{email: string, password: string}} params */
export const login = async ({ email, password }) => {
  // AWS Cognito / Lambda / AppSync などに合わせてエンドポイント実装
  const resp = await awsClient.post(`${authBasePath}/login`, { email, password });
  const { accessToken } = resp.data;
  if (!accessToken) {
    throw new Error('AWS 認証トークンが応答に含まれていません');
  }
  setAuthToken(accessToken);
  return resp.data;
};

export const logout = () => {
  clearAuthToken();
  localStorage.removeItem('discord_user');
};

export const refreshToken = async () => {
  const resp = await awsClient.post(`${authBasePath}/refresh-token`);
  const { accessToken } = resp.data;
  if (accessToken) {
    setAuthToken(accessToken);
  }
  return resp.data;
};

const getCurrentDiscordRedirectUri = () => `${window.location.origin}/OAuthCallback`;

export const getDiscordAuthUrl = () => {
  const clientId = /** @type {string} */ (import.meta.env.VITE_DISCORD_CLIENT_ID);
  const redirectUri = getCurrentDiscordRedirectUri();

  // guilds.members.read はロール判定（本科/通信・admin）に必須。
  const scope = encodeURIComponent(
    import.meta.env.VITE_DISCORD_SCOPE ?? 'identify email guilds.members.read'
  );
  const state = encodeURIComponent(`discord_${Date.now()}`);

  return `https://discord.com/api/oauth2/authorize?response_type=code&client_id=${clientId}&scope=${scope}&redirect_uri=${encodeURIComponent(redirectUri)}&prompt=consent&state=${state}`;
};

/** @param {string} code */
export const exchangeDiscordCode = async (code) => {
  const apiBaseUrl = (/** @type {string} */ (import.meta.env.VITE_API_BASE_URL) ?? '').replace(/\/$/, '');
  const exchangeUrl = /** @type {string} */ (import.meta.env.VITE_DISCORD_AUTH_EXCHANGE_URL || `${apiBaseUrl}/auth/discord/callback`);

  const response = await fetch(exchangeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code, redirectUri: getCurrentDiscordRedirectUri() }),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (err) {
    throw new Error('Lambda response is not JSON');
  }

  if (!response.ok) {
    const reason = payload?.error || 'code_exchange_failed';
    const detail = payload?.detail ? `: ${payload.detail}` : '';
    throw new Error(`${reason}${detail}`);
  }

  const accessToken = payload?.accessToken;
  const user = normalizeDiscordUser(payload?.user);

  if (!accessToken || !user) {
    throw new Error('Lambda response missing accessToken or user');
  }

  setAuthToken(accessToken);
  localStorage.setItem('discord_user', JSON.stringify(user));
  return { accessToken, user };
};

export const isAuthenticated = () => {
  return Boolean(
    localStorage.getItem('aws_access_token') ||
    localStorage.getItem('discord_user')
  );
};

export const getCurrentUser = async () => {
  const userStr = localStorage.getItem('discord_user');
  if (userStr) {
    try {
      return normalizeDiscordUser(JSON.parse(userStr));
    } catch (err) {
      console.warn('Failed to parse discord_user in localStorage', err);
      localStorage.removeItem('discord_user');
      return null;
    }
  }

  return null;
};
